import * as React from 'react';
import { Column, Row } from '../../../components/grid';
import { useInputValidation } from '../../../validation';
import type { INumberFormatInputProps } from './NumberFormatInput';
import { NumberFormatInput } from './NumberFormatInput';

type InputPropsWithoutOnChange = Omit<INumberFormatInputProps, 'onChange'>;

interface Props extends InputPropsWithoutOnChange {
  onChange: (accountNumber: string) => void;
  focus?: boolean;
}

const onlyNumber = /^\d+$/;

export const AccountInput: React.FC<Props> = ({ onChange, ...rest }) => {
  const [bankNr, setBankNr] = React.useState('');
  const [ledger, setLedger] = React.useState('');
  const [accountNr, setAccountNr] = React.useState('');

  const [bankNrRef, isBankNrValid] = useInputValidation(bankNr, [
    {
      validation: (val) => val.length === 4,
    },
  ]);
  const [ledgerRef, isLedgerValid] = useInputValidation(ledger, [
    {
      validation: (val) => val.length === 2,
    },
  ]);

  const [accountNrRef, isAccountNumberValid] = useInputValidation(accountNr, [
    {
      validation: (val) => {
        return val.length === 6;
      },
    },
  ]);

  React.useEffect(() => {
    onChange(`${bankNr}${ledger}${accountNr}`);
  }, [bankNr, ledger, accountNr]);

  const onBankNrChange = (value: string) => {
    if (value.length <= 4) {
      setBankNr(value);
      if (value.length === 4) {
        setTimeout(() => {
          ledgerRef.current?.focus();
        }, 0);
      }
    }
  };

  const onLedgerChange = (value: string) => {
    if (value.length <= 2) {
      setLedger(value);
      if (value.length === 2) {
        setTimeout(() => {
          accountNrRef.current?.focus();
        }, 0);
      }
      if (value.length === 0) {
        setTimeout(() => {
          bankNrRef.current?.focus();
        }, 0);
      }
    }
  };

  const onAccountNrChange = (value: string) => {
    if (value.length <= 6) {
      setAccountNr(value);
    }
    if (value.length === 0) {
      setTimeout(() => {
        ledgerRef.current?.focus();
      }, 0);
    }
  };

  const onPaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    const clipboardData = event.clipboardData.getData('Text').trim();
    const accountNumber = clipboardData.replace(/\D/g, '');
    if (accountNumber.length === 12 && onlyNumber.test(accountNumber)) {
      const bankNr = accountNumber.slice(0, 4);
      const ledger = accountNumber.slice(4, 6);
      const accountNr = accountNumber.slice(6, 12);
      setBankNr(bankNr);
      setLedger(ledger);
      setAccountNr(accountNr);
      accountNrRef.current?.focus();
    }
  };

  const onEnter = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      const length = accountNr.length;
      if (length < 6 && length !== 0) {
        setAccountNr(`${'0'.repeat(6 - length)}${accountNr}`);
      }
    }
    return true;
  };

  return (
    <Row>
      <Column md={{ width: 4.5 / 12 }}>
        <NumberFormatInput
          {...rest}
          id={'bankNr'}
          label="Banki"
          maxLength={4}
          onValueChange={({ value }) => onBankNrChange(value)}
          value={bankNr}
          onBlur={() => {
            if (bankNr.length === 3) {
              setBankNr(`0${bankNr}`);
            }
          }}
          onPaste={onPaste}
          isValid={isBankNrValid}
          inputRef={bankNrRef}
          allowLeadingZeros
        />
      </Column>
      <Column md={{ width: 3 / 12 }}>
        <NumberFormatInput
          {...rest}
          id={'hbNr'}
          inputRef={ledgerRef}
          label="Höfuðbók"
          value={ledger}
          onBlur={() => {
            if (ledger.length === 1) {
              setLedger(`0${ledger}`);
            }
          }}
          maxLength={2}
          onValueChange={({ value }) => onLedgerChange(value)}
          onPaste={onPaste}
          isValid={isLedgerValid}
          allowLeadingZeros
          format={undefined}
        />
      </Column>
      <Column md={{ width: 4.5 / 12 }}>
        <NumberFormatInput
          {...rest}
          id={'accountNr'}
          value={accountNr}
          label="Reikningsnúmer"
          onKeyDown={onEnter}
          maxLength={6}
          onValueChange={({ value }) => onAccountNrChange(value)}
          inputRef={accountNrRef}
          onBlur={() => {
            if (accountNr && accountNr.length < 6 && accountNr.length !== 0) {
              setAccountNr(`${'0'.repeat(6 - accountNr.length)}${accountNr}`);
            }
          }}
          onPaste={onPaste}
          isValid={isAccountNumberValid}
          allowLeadingZeros
          format={(val) => val}
          errorMessage={
            isBankNrValid === false ||
            isLedgerValid === false ||
            isAccountNumberValid === false
              ? 'Reikningsnúmer ekki á réttu formi'
              : undefined
          }
        />
      </Column>
    </Row>
  );
};
